import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaArrowLeft, FaCalendarAlt, FaClock, FaUsers, FaStar, FaPlay, FaBook, FaLightbulb } from 'react-icons/fa';
import { isAudioSupported, playSmartAudio } from '../utils/audioUtils';

interface Activity {
  _id?: string;
  title: string;
  description: string;
  type: 'craft' | 'game' | 'story' | 'quiz';
  instructions: string[];
  materials?: string[];
  imageUrl?: string;
}

interface HolidayPack {
  _id: string;
  title: string;
  description: string;
  holiday: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  estimatedTime: number;
  wordCount: number;
  activities: Activity[];
  culturalNotes: string;
  vocabulary: Array<{
    farsi: string;
    transliteration: string;
    english: string;
    audioUrl?: string;
  }>;
  phrases: Array<{
    farsi: string;
    transliteration: string;
    english: string;
    context: string;
  }>;
  stories: Array<{
    title: string;
    content: string;
    moral: string;
  }>;
  games: Array<{
    name: string;
    description: string;
    instructions: string;
  }>;
  createdAt: string;
}

const HolidayPackDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [holidayPack, setHolidayPack] = useState<HolidayPack | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'vocabulary' | 'phrases' | 'stories' | 'games'>('overview');

  useEffect(() => {
    if (id) {
      fetchHolidayPack(id);
    }
  }, [id]);

  const fetchHolidayPack = async (packId: string) => {
    try {
      const response = await fetch(`/api/cultural/holidays/${packId}`);
      const data = await response.json();
      
      if (data.success) {
        setHolidayPack(data.data);
      } else {
        setError(data.message || 'Failed to fetch holiday pack');
      }
    } catch (err) {
      setError('Failed to fetch holiday pack');
      console.error('Error fetching holiday pack:', err);
    } finally {
      setLoading(false);
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'intermediate': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'advanced': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getDifficultyText = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'مبتدی';
      case 'intermediate': return 'متوسط';
      case 'advanced': return 'پیشرفته';
      default: return difficulty;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error || !holidayPack) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500 text-xl mb-4">⚠️</div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">خطا در بارگذاری</h2>
        <p className="text-gray-600 dark:text-gray-400">{error || 'Holiday pack not found'}</p>
        <Link 
          to="/cultural/holidays"
          className="mt-4 inline-block px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          بازگشت به لیست
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 p-6">
      <div className="max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-center space-x-4 rtl:space-x-reverse mb-6">
        <Link
          to="/cultural/holidays"
          className="p-2 text-gray-600 dark:text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 transition-colors"
        >
          <FaArrowLeft />
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {holidayPack.title}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {holidayPack.holiday}
          </p>
        </div>
      </div>

      {/* Pack Info */}
      <div className="bg-white rounded-2xl p-6 shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getDifficultyColor(holidayPack.difficulty)}`}>
            {getDifficultyText(holidayPack.difficulty)}
          </span>
          <FaCalendarAlt className="text-blue-500" />
        </div>
        
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          {holidayPack.description}
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center text-gray-600 dark:text-gray-400">
            <FaClock className="mr-2" />
            <span>{holidayPack.estimatedTime} دقیقه</span>
          </div>
          <div className="flex items-center text-gray-600 dark:text-gray-400">
            <FaUsers className="mr-2" />
            <span>{holidayPack.wordCount} کلمه</span>
          </div>
          <div className="flex items-center text-gray-600 dark:text-gray-400">
            <FaStar className="mr-2" />
            <span>{holidayPack.activities.length} فعالیت</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 rtl:space-x-reverse px-6">
            {[
              { id: 'overview', label: 'نمای کلی', icon: FaBook },
              { id: 'vocabulary', label: 'واژگان', icon: FaBook },
              { id: 'phrases', label: 'عبارات', icon: FaBook },
              { id: 'stories', label: 'داستان‌ها', icon: FaBook },
              { id: 'games', label: 'بازی‌ها', icon: FaPlay }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                <tab.icon className="mr-2" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                  فعالیت‌های این بسته
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {(holidayPack.activities || []).map((activity, index) => (
                    <div key={activity._id || index} className="flex items-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <div className="w-8 h-8 bg-blue-100 dark:bg-blue-800 rounded-full flex items-center justify-center mr-3">
                        <span className="text-blue-600 dark:text-blue-400 font-bold">{index + 1}</span>
                      </div>
                      <div className="flex-1">
                        <span className="text-gray-900 dark:text-white font-medium">{activity.title}</span>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{activity.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                  نکات فرهنگی
                </h3>
                <div className="bg-gray-50 dark:bg-gray-900/20 rounded-lg p-4">
                  <div className="flex items-start">
                    <FaLightbulb className="text-blue-500 mt-1 mr-3 flex-shrink-0" />
                    <p className="text-gray-900 dark:text-white leading-relaxed">
                      {holidayPack.culturalNotes}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Vocabulary Tab */}
          {activeTab === 'vocabulary' && (
            <div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                واژگان مرتبط با {holidayPack.holiday}
              </h3>
              
              {/* General Nowruz Vocabulary */}
              <div className="mb-8">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                  <span className="mr-2">🌸</span>
                  واژگان عمومی نوروز (General Nowruz Vocabulary)
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { farsi: 'نوروز', transliteration: 'nowruz', english: 'Persian New Year' },
                    { farsi: 'سال نو', transliteration: 'sāle no', english: 'New Year' },
                    { farsi: 'بهار', transliteration: 'bahār', english: 'Spring' },
                    { farsi: 'عید', transliteration: 'eid', english: 'Celebration / Holiday' },
                    { farsi: 'تقویم هجری شمسی', transliteration: 'taghvime hejri-ye shamsi', english: 'Solar Hijri Calendar' },
                    { farsi: 'تحویل سال', transliteration: 'tahvile sāl', english: 'Turn of the year (New Year moment)' },
                    { farsi: 'جشن', transliteration: 'jashn', english: 'Celebration' },
                    { farsi: 'دید و بازدید', transliteration: 'did o bāzdid', english: 'Visiting family/friends' },
                    { farsi: 'سفره هفت‌سین', transliteration: 'sofre-ye haft-sin', english: 'Haft-Seen table' },
                    { farsi: 'تعطیلات نوروزی', transliteration: 'ta\'tilāt-e nowruzi', english: 'Nowruz holidays' }
                  ].map((word, index) => (
                    <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-lg font-bold text-gray-900 dark:text-white">
                          {word.farsi}
                        </h4>
                        {isAudioSupported() && (
                          <button
                            onClick={() => playSmartAudio(undefined, word.farsi).catch(console.error)}
                            className="p-2 text-blue-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                          >
                            <FaPlay />
                          </button>
                        )}
                      </div>
                      <p className="text-sm mb-1">
                        {word.transliteration}
                      </p>
                      <p className="text-lg font-semibold">
                        {word.english}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Haft-Seen Items */}
              <div className="mb-8">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                  <span className="mr-2">🥒</span>
                  اجزای سفره هفت‌سین (Haft-Seen Items)
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { farsi: 'سیب', transliteration: 'sib', english: 'Apple – health' },
                    { farsi: 'سبزه', transliteration: 'sabze', english: 'Sprouts – rebirth' },
                    { farsi: 'سمنو', transliteration: 'samanu', english: 'Wheat pudding – power & sweetness' },
                    { farsi: 'سیر', transliteration: 'sir', english: 'Garlic – medicine/protection' },
                    { farsi: 'سنجد', transliteration: 'senjed', english: 'Dried oleaster – love' },
                    { farsi: 'سرکه', transliteration: 'serkeh', english: 'Vinegar – patience' },
                    { farsi: 'سکه', transliteration: 'sekkeh', english: 'Coin – wealth' },
                    { farsi: 'ساعت', transliteration: 'sā\'at', english: 'Clock – time (optional item)' },
                    { farsi: 'شمع', transliteration: 'sham\'', english: 'Candle – light' }
                  ].map((word, index) => (
                    <div key={index} className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-lg font-bold text-gray-900 dark:text-white">
                          {word.farsi}
                        </h4>
                        {isAudioSupported() && (
                          <button
                            onClick={() => playSmartAudio(undefined, word.farsi).catch(console.error)}
                            className="p-2 text-blue-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                          >
                            <FaPlay />
                          </button>
                        )}
                      </div>
                      <p className="text-sm mb-1">
                        {word.transliteration}
                      </p>
                      <p className="text-lg font-semibold">
                        {word.english}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Traditions & Customs */}
              <div className="mb-8">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                  <span className="mr-2">🧹</span>
                  آداب و رسوم (Traditions & Customs)
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { farsi: 'خانه‌تکانی', transliteration: 'khāneh takāni', english: 'Spring cleaning' },
                    { farsi: 'عیدی دادن', transliteration: 'eydi dādan', english: 'Giving New Year gifts/money' },
                    { farsi: 'لباس نو', transliteration: 'lebās-e no', english: 'New clothes' },
                    { farsi: 'چهارشنبه‌سوری', transliteration: 'chahārshanbe-suri', english: 'Fire-jumping festival (last Wed. before Nowruz)' },
                    { farsi: 'سیزده‌به‌در', transliteration: 'sizdah-be-dar', english: '13th day picnic (nature day)' },
                    { farsi: 'فال حافظ', transliteration: 'fāl-e Hāfez', english: 'Divination with Hafez\'s poetry' }
                  ].map((word, index) => (
                    <div key={index} className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-lg font-bold text-gray-900 dark:text-white">
                          {word.farsi}
                        </h4>
                        {isAudioSupported() && (
                          <button
                            onClick={() => playSmartAudio(undefined, word.farsi).catch(console.error)}
                            className="p-2 text-blue-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                          >
                            <FaPlay />
                          </button>
                        )}
                      </div>
                      <p className="text-sm mb-1">
                        {word.transliteration}
                      </p>
                      <p className="text-lg font-semibold">
                        {word.english}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Family Members */}
              <div className="mb-8">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                  <span className="mr-2">🧕</span>
                  افراد خانواده (Family Members for Nowruz)
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { farsi: 'پدربزرگ', transliteration: 'pedarbozorg', english: 'Grandfather' },
                    { farsi: 'مادربزرگ', transliteration: 'mādarbozorg', english: 'Grandmother' },
                    { farsi: 'عمو', transliteration: 'amu', english: 'Uncle (father\'s side)' },
                    { farsi: 'خاله', transliteration: 'khāle', english: 'Aunt (mother\'s side)' },
                    { farsi: 'بچه‌ها', transliteration: 'bachehā', english: 'Children' },
                    { farsi: 'مهمان', transliteration: 'mehmān', english: 'Guest' }
                  ].map((word, index) => (
                    <div key={index} className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-lg font-bold text-gray-900 dark:text-white">
                          {word.farsi}
                        </h4>
                        {isAudioSupported() && (
                          <button
                            onClick={() => playSmartAudio(undefined, word.farsi).catch(console.error)}
                            className="p-2 text-blue-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                          >
                            <FaPlay />
                          </button>
                        )}
                      </div>
                      <p className="text-sm mb-1">
                        {word.transliteration}
                      </p>
                      <p className="text-lg font-semibold">
                        {word.english}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Time Words */}
              <div className="mb-8">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                  <span className="mr-2">⏳</span>
                  واژگان زمانی (Time Words)
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { farsi: 'اول فروردین', transliteration: 'avval-e farvardin', english: 'First of Farvardin (New Year\'s Day)' },
                    { farsi: 'پایان سال', transliteration: 'pāyān-e sāl', english: 'End of the year' },
                    { farsi: 'فصل بهار', transliteration: 'fasl-e bahār', english: 'Spring season' },
                    { farsi: 'صبح سال نو', transliteration: 'sobh-e sāle no', english: 'Morning of the new year' }
                  ].map((word, index) => (
                    <div key={index} className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-lg font-bold text-gray-900 dark:text-white">
                          {word.farsi}
                        </h4>
                        {isAudioSupported() && (
                          <button
                            onClick={() => playSmartAudio(undefined, word.farsi).catch(console.error)}
                            className="p-2 text-blue-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                          >
                            <FaPlay />
                          </button>
                        )}
                      </div>
                      <p className="text-sm mb-1">
                        {word.transliteration}
                      </p>
                      <p className="text-lg font-semibold">
                        {word.english}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Original vocabulary from API (if any) */}
              {(holidayPack.vocabulary || []).length > 0 && (
                <div className="mb-8">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    واژگان اضافی (Additional Vocabulary)
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {(holidayPack.vocabulary || []).map((word, index) => (
                      <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-lg font-bold text-gray-900 dark:text-white">
                            {word.farsi}
                          </h4>
                          {isAudioSupported() && (
                            <button
                              onClick={() => playSmartAudio(undefined, word.farsi).catch(console.error)}
                              className="p-2 text-blue-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                            >
                              <FaPlay />
                            </button>
                          )}
                        </div>
                        <p className="text-sm mb-1">
                          {word.transliteration}
                        </p>
                        <p className="text-lg font-semibold">
                          {word.english}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Phrases Tab */}
          {activeTab === 'phrases' && (
            <div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                عبارات مفید
              </h3>
              
              <div className="space-y-6">
                {/* Phrase 1 */}
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-lg font-bold text-gray-900 dark:text-white">
                      نوروز مبارک
                    </h4>
                    {isAudioSupported() && (
                      <button
                        onClick={() => playSmartAudio(undefined, 'نوروز مبارک').catch(console.error)}
                        className="p-2 text-blue-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                      >
                        <FaPlay />
                      </button>
                    )}
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 text-sm mb-2">
                    <strong>تلفظ:</strong> Nowruz mobārak
                  </p>
                  <p className="text-gray-900 dark:text-white mb-3">
                    <strong>معنی:</strong> Happy Nowruz
                  </p>
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 mb-3">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      <strong>🔹 کاربرد:</strong> برای تبریک آغاز سال نو ایرانی (نوروز) استفاده می‌شود.
                    </p>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                      <strong>🔹 مثال:</strong>
                    </p>
                    <p className="text-gray-900 dark:text-white mb-1">– نوروز مبارک! امیدوارم سال خوبی داشته باشی.</p>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-2 ltr text-left">– Nowruz mobarak! Omidvār-am sāle khoobi dāshte bāshi.</p>
                    <p className="text-gray-600 dark:text-gray-400 text-sm ltr text-left italic">(Translation: Happy Nowruz! I hope you have a good year.)</p>
                  </div>
                </div>

                {/* Phrase 2 */}
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-lg font-bold text-gray-900 dark:text-white">
                      سال نو مبارک
                    </h4>
                    {isAudioSupported() && (
                      <button
                        onClick={() => playSmartAudio(undefined, 'سال نو مبارک').catch(console.error)}
                        className="p-2 text-blue-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                      >
                        <FaPlay />
                      </button>
                    )}
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 text-sm mb-2">
                    <strong>تلفظ:</strong> Sāl-e now mobārak
                  </p>
                  <p className="text-gray-900 dark:text-white mb-3">
                    <strong>معنی:</strong> Happy New Year
                  </p>
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 mb-3">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      <strong>🔹 کاربرد:</strong> مثل "نوروز مبارک"، اما تأکید بر سال جدید دارد.
                    </p>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                      <strong>🔹 مثال:</strong>
                    </p>
                    <p className="text-gray-900 dark:text-white mb-1">– سال نو مبارک! پر از شادی و موفقیت باشه!</p>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-2 ltr text-left">– Sāl-e now mobārak! Por az shādi o movafaghiat bāshe!</p>
                    <p className="text-gray-600 dark:text-gray-400 text-sm ltr text-left italic">(Translation: Happy New Year! May it be full of joy and success!)</p>
                  </div>
                </div>

                {/* Phrase 3 */}
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-lg font-bold text-gray-900 dark:text-white">
                      هفت‌سین
                    </h4>
                    {isAudioSupported() && (
                      <button
                        onClick={() => playSmartAudio(undefined, 'هفت‌سین').catch(console.error)}
                        className="p-2 text-blue-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                      >
                        <FaPlay />
                      </button>
                    )}
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 text-sm mb-2">
                    <strong>تلفظ:</strong> Haft-seen
                  </p>
                  <p className="text-gray-900 dark:text-white mb-3">
                    <strong>معنی:</strong> Haftseen Table
                  </p>
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 mb-3">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      <strong>🔹 کاربرد:</strong> نام سفره‌ای سنتی در نوروز شامل ۷ چیز که با حرف "س" شروع می‌شوند.
                    </p>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                      <strong>🔹 مثال:</strong>
                    </p>
                    <p className="text-gray-900 dark:text-white mb-1">– مامانم امسال هفت‌سین خیلی قشنگی چید.</p>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-2 ltr text-left">– Mānam emsāl haft-seen-e khayli ghashangi chid.</p>
                    <p className="text-gray-600 dark:text-gray-400 text-sm ltr text-left italic">(My mom set a beautiful Haftseen this year.)</p>
                  </div>
                </div>

                {/* Phrase 4 */}
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-lg font-bold text-gray-900 dark:text-white">
                      عیدی
                    </h4>
                    {isAudioSupported() && (
                      <button
                        onClick={() => playSmartAudio(undefined, 'عیدی').catch(console.error)}
                        className="p-2 text-blue-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                      >
                        <FaPlay />
                      </button>
                    )}
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 text-sm mb-2">
                    <strong>تلفظ:</strong> Eydi
                  </p>
                  <p className="text-gray-900 dark:text-white mb-3">
                    <strong>معنی:</strong> New Year Gift / Cash Gift
                  </p>
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 mb-3">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      <strong>🔹 کاربرد:</strong> پول یا هدیه‌ای که بزرگ‌ترها به کوچکترها می‌دهند.
                    </p>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                      <strong>🔹 مثال:</strong>
                    </p>
                    <p className="text-gray-900 dark:text-white mb-1">– دایی‌ام بهم عیدی داد!</p>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-2 ltr text-left">– Dāyi-am behem eydi dād!</p>
                    <p className="text-gray-600 dark:text-gray-400 text-sm ltr text-left italic">(My uncle gave me an Eydi!)</p>
                  </div>
                </div>

                {/* Phrase 5 */}
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-lg font-bold text-gray-900 dark:text-white">
                      خانه‌تکانی
                    </h4>
                    {isAudioSupported() && (
                      <button
                        onClick={() => playSmartAudio(undefined, 'خانه‌تکانی').catch(console.error)}
                        className="p-2 text-blue-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                      >
                        <FaPlay />
                      </button>
                    )}
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 text-sm mb-2">
                    <strong>تلفظ:</strong> Khāneh takāni
                  </p>
                  <p className="text-gray-900 dark:text-white mb-3">
                    <strong>معنی:</strong> Spring Cleaning
                  </p>
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 mb-3">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      <strong>🔹 کاربرد:</strong> سنت تمیز کردن خانه قبل از نوروز.
                    </p>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                      <strong>🔹 مثال:</strong>
                    </p>
                    <p className="text-gray-900 dark:text-white mb-1">– دیروز با مامان خانه‌تکانی کردیم.</p>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-2 ltr text-left">– Dirooz bā māman khāneh takāni kardim.</p>
                    <p className="text-gray-600 dark:text-gray-400 text-sm ltr text-left italic">(We did spring cleaning yesterday with mom.)</p>
                  </div>
                </div>

                {/* Phrase 6 */}
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-lg font-bold text-gray-900 dark:text-white">
                      سیزده‌به‌در
                    </h4>
                    {isAudioSupported() && (
                      <button
                        onClick={() => playSmartAudio(undefined, 'سیزده‌به‌در').catch(console.error)}
                        className="p-2 text-blue-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                      >
                        <FaPlay />
                      </button>
                    )}
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 text-sm mb-2">
                    <strong>تلفظ:</strong> Sizdah-be-dar
                  </p>
                  <p className="text-gray-900 dark:text-white mb-3">
                    <strong>معنی:</strong> Nature Day (13th Day of Nowruz)
                  </p>
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 mb-3">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      <strong>🔹 کاربرد:</strong> روز سیزدهم فروردین که مردم به طبیعت می‌روند.
                    </p>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                      <strong>🔹 مثال:</strong>
                    </p>
                    <p className="text-gray-900 dark:text-white mb-1">– سیزده‌به‌در رفتیم پارک و کلی بازی کردیم.</p>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-2 ltr text-left">– Sizdah-be-dar raftim pārk o koli bāzi kardim.</p>
                    <p className="text-gray-600 dark:text-gray-400 text-sm ltr text-left italic">(We went to the park on Sizdah-be-dar and played a lot.)</p>
                  </div>
                </div>

                {/* Phrase 7 */}
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-lg font-bold text-gray-900 dark:text-white">
                      چهارشنبه‌سوری
                    </h4>
                    {isAudioSupported() && (
                      <button
                        onClick={() => playSmartAudio(undefined, 'چهارشنبه‌سوری').catch(console.error)}
                        className="p-2 text-blue-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                      >
                        <FaPlay />
                      </button>
                    )}
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 text-sm mb-2">
                    <strong>تلفظ:</strong> Chahārshanbeh-Suri
                  </p>
                  <p className="text-gray-900 dark:text-white mb-3">
                    <strong>معنی:</strong> Fire-Jumping Festival
                  </p>
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 mb-3">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      <strong>🔹 کاربرد:</strong> جشن قبل از نوروز برای پاکسازی و شادی.
                    </p>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                      <strong>🔹 مثال:</strong>
                    </p>
                    <p className="text-gray-900 dark:text-white mb-1">– من از روی آتش پریدم!</p>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-2 ltr text-left">– Man az rooye ātash paridam!</p>
                    <p className="text-gray-600 dark:text-gray-400 text-sm ltr text-left italic">(I jumped over the fire!)</p>
                  </div>
                </div>

                {/* Phrase 8 */}
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-lg font-bold text-gray-900 dark:text-white">
                      زردی من از تو، سرخی تو از من
                    </h4>
                    {isAudioSupported() && (
                      <button
                        onClick={() => playSmartAudio(undefined, 'زردی من از تو، سرخی تو از من').catch(console.error)}
                        className="p-2 text-blue-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                      >
                        <FaPlay />
                      </button>
                    )}
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 text-sm mb-2">
                    <strong>تلفظ:</strong> Zardi-ye man az to, sorkhi-ye to az man
                  </p>
                  <p className="text-gray-900 dark:text-white mb-3">
                    <strong>معنی:</strong> "My yellow (sickness) to you, your red (health) to me"
                  </p>
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 mb-3">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      <strong>🔹 کاربرد:</strong> گفته‌ای سنتی هنگام پریدن از آتش در چهارشنبه‌سوری.
                    </p>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                      <strong>🔹 مثال:</strong>
                    </p>
                    <p className="text-gray-900 dark:text-white mb-1">– همه با صدای بلند می‌گفتن: زردی من از تو، سرخی تو از من!</p>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-2 ltr text-left">– Hame bā sedāye boland migoftan...</p>
                    <p className="text-gray-600 dark:text-gray-400 text-sm ltr text-left italic">(Everyone was saying loudly: "My yellow to you, your red to me!")</p>
                  </div>
                </div>

                {/* Phrase 9 */}
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-lg font-bold text-gray-900 dark:text-white">
                      سفره هفت‌سین چیدی؟
                    </h4>
                    {isAudioSupported() && (
                      <button
                        onClick={() => playSmartAudio(undefined, 'سفره هفت‌سین چیدی؟').catch(console.error)}
                        className="p-2 text-blue-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                      >
                        <FaPlay />
                      </button>
                    )}
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 text-sm mb-2">
                    <strong>تلفظ:</strong> Sofre-ye haft-seen chidi?
                  </p>
                  <p className="text-gray-900 dark:text-white mb-3">
                    <strong>معنی:</strong> Did you set the Haftseen table?
                  </p>
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 mb-3">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      <strong>🔹 کاربرد:</strong> سؤال دوستانه برای پرسیدن درباره آماده‌سازی نوروز.
                    </p>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                      <strong>🔹 مثال:</strong>
                    </p>
                    <p className="text-gray-900 dark:text-white mb-1">– آره! امسال یه سمنو خوشمزه هم داریم!</p>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-2 ltr text-left">– Āre! Emsāl ye samanu khoshmazze ham dārim!</p>
                    <p className="text-gray-600 dark:text-gray-400 text-sm ltr text-left italic">(Yes! We even have delicious samanu this year!)</p>
                  </div>
                </div>

                {/* Phrase 10 */}
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-lg font-bold text-gray-900 dark:text-white">
                      امیدوارم سال خوبی داشته باشی
                    </h4>
                    {isAudioSupported() && (
                      <button
                        onClick={() => playSmartAudio(undefined, 'امیدوارم سال خوبی داشته باشی').catch(console.error)}
                        className="p-2 text-blue-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                      >
                        <FaPlay />
                      </button>
                    )}
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 text-sm mb-2">
                    <strong>تلفظ:</strong> Omidvār-am sāle khoobi dāshte bāshi
                  </p>
                  <p className="text-gray-900 dark:text-white mb-3">
                    <strong>معنی:</strong> I hope you have a good year
                  </p>
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 mb-3">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      <strong>🔹 کاربرد:</strong> دعای زیبای نوروزی
                    </p>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                      <strong>🔹 مثال:</strong>
                    </p>
                    <p className="text-gray-900 dark:text-white mb-1">– نوروز مبارک! امیدوارم سال خوبی داشته باشی.</p>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-2 ltr text-left">– Nowruz mobārak! Omidvār-am sāle khoobi dāshte bāshi.</p>
                    <p className="text-gray-600 dark:text-gray-400 text-sm ltr text-left italic">(Happy Nowruz! I hope you have a good year.)</p>
                  </div>
                </div>

                {/* Original phrases from API (if any) */}
                {(holidayPack.phrases || []).length > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      عبارات اضافی (Additional Phrases)
                    </h4>
                    <div className="space-y-4">
                      {(holidayPack.phrases || []).map((phrase, index) => (
                        <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-lg font-bold text-gray-900 dark:text-white">
                              {phrase.farsi}
                            </h4>
                            {isAudioSupported() && (
                              <button
                                onClick={() => playSmartAudio(undefined, phrase.farsi).catch(console.error)}
                                className="p-2 text-blue-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                              >
                                <FaPlay />
                              </button>
                            )}
                          </div>
                          <p className="text-sm mb-1">
                            {phrase.transliteration}
                          </p>
                          <p className="text-lg font-semibold">
                            {phrase.english}
                          </p>
                          <p className="text-sm text-gray-500">
                            <strong>کاربرد:</strong> {phrase.context}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Stories Tab */}
          {activeTab === 'stories' && (
            <div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                داستان‌های مرتبط
              </h3>
              
              {/* Beginner Story */}
              <div className="mb-8">
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-6 mb-4">
                  <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-2 flex items-center">
                    <span className="mr-2">📘</span>
                    داستان ۱ (سطح مبتدی): سیب قرمز مامان‌بزرگ
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    <strong>کلمات کلیدی:</strong> سیب، سبزه، سفره هفت‌سین، نوروز، مادربزرگ
                  </p>
                </div>
                
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg">
                  <h5 className="font-semibold text-gray-900 dark:text-white mb-3">داستان:</h5>
                  <div className="space-y-3 text-gray-900 dark:text-white leading-relaxed">
                    <p>امروز نوروز است. نازنین با مامان و بابا به خانه‌ی مامان‌بزرگ می‌رود.</p>
                    <p>مامان‌بزرگ سفره‌ی هفت‌سین چیده است.</p>
                    <p>نازنین می‌گوید: "وای! چه سفره‌ی قشنگی!"</p>
                    <p>روی سفره، سیب، سبزه، سمنو و سکه هست.</p>
                    <p>مامان‌بزرگ یک سیب قرمز به نازنین می‌دهد.</p>
                    <p>می‌گوید: "سال نو مبارک، نازنین جان!"</p>
                    <p>نازنین لبخند می‌زند و می‌گوید: "ممنون مامان‌بزرگ! سال نو مبارک!"</p>
                  </div>
                  
                  <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <h6 className="font-semibold text-gray-900 dark:text-white mb-2">English Translation:</h6>
                    <div className="space-y-2 text-gray-700 dark:text-gray-300 text-sm ltr text-left">
                      <p>Today is Nowruz. Nazanin goes to her grandmother's house with her mother and father.</p>
                      <p>Grandmother has set up the Haft-Seen table.</p>
                      <p>Nazanin says: "Wow! What a beautiful table!"</p>
                      <p>On the table, there are apple, sprouts, samanu, and coins.</p>
                      <p>Grandmother gives Nazanin a red apple.</p>
                      <p>She says: "Happy New Year, dear Nazanin!"</p>
                      <p>Nazanin smiles and says: "Thank you, grandmother! Happy New Year!"</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Intermediate Story */}
              <div className="mb-8">
                <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-6 mb-4">
                  <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-2 flex items-center">
                    <span className="mr-2">📙</span>
                    داستان ۲ (سطح متوسط): چهارشنبه‌سوری و ترس علی
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    <strong>کلمات کلیدی:</strong> چهارشنبه‌سوری، آتش، پریدن، شجاعت، خانواده
                  </p>
                </div>
                
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg">
                  <h5 className="font-semibold text-gray-900 dark:text-white mb-3">داستان:</h5>
                  <div className="space-y-3 text-gray-900 dark:text-white leading-relaxed">
                    <p>علی یک پسر هشت‌ساله است. امشب چهارشنبه‌سوری است.</p>
                    <p>بابا و مامان و خواهرش در پارک هستند. مردم آتش روشن کرده‌اند.</p>
                    <p>همه از روی آتش می‌پرند و می‌گویند: "زردی من از تو، سرخی تو از من!"</p>
                    <p>علی می‌ترسد. دلش نمی‌خواهد بپرد.</p>
                    <p>مامان دست علی را می‌گیرد و می‌گوید: "با هم می‌پریم."</p>
                    <p>علی می‌خندد و با مامان می‌پرد.</p>
                    <p>او احساس شجاعت می‌کند و می‌گوید: "من از آتش نمی‌ترسم!"</p>
                  </div>
                  
                  <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <h6 className="font-semibold text-gray-900 dark:text-white mb-2">English Translation:</h6>
                    <div className="space-y-2 text-gray-700 dark:text-gray-300 text-sm ltr text-left">
                      <p>Ali is an eight-year-old boy. Tonight is Chaharshanbe Suri.</p>
                      <p>His father, mother, and sister are in the park. People have lit fires.</p>
                      <p>Everyone jumps over the fire and says: "My yellowness to you, your redness to me!"</p>
                      <p>Ali is afraid. He doesn't want to jump.</p>
                      <p>Mother takes Ali's hand and says: "We'll jump together."</p>
                      <p>Ali laughs and jumps with his mother.</p>
                      <p>He feels brave and says: "I'm not afraid of fire!"</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Advanced Story */}
              <div className="mb-8">
                <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-6 mb-4">
                  <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-2 flex items-center">
                    <span className="mr-2">📗</span>
                    داستان ۳ (سطح پیشرفته): فال حافظ شب سال نو
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    <strong>کلمات کلیدی:</strong> حافظ، شعر، عیدی، خانواده، سفره هفت‌سین
                  </p>
                </div>
                
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg">
                  <h5 className="font-semibold text-gray-900 dark:text-white mb-3">داستان:</h5>
                  <div className="space-y-3 text-gray-900 dark:text-white leading-relaxed">
                    <p>در شب سال نو، خانواده‌ی راد دور سفره‌ی هفت‌سین نشسته‌اند.</p>
                    <p>پدر می‌گوید: "بیایید فال حافظ بگیریم!"</p>
                    <p>مادر دیوان حافظ را می‌آورد. نوبت نازلی است.</p>
                    <p>او چشم‌هایش را می‌بندد و آرزو می‌کند.</p>
                    <p>سپس کتاب را باز می‌کند. شعر این است:</p>
                    <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4 my-4 text-center">
                      <p className="text-gray-900 dark:text-white font-medium italic">
                        "بهار آمد که دل‌ها را ببخشد،<br/>
                        گلی از خاک ما بردارد و خشکند."
                      </p>
                    </div>
                    <p>نازلی لبخند می‌زند. پدر می‌گوید: "فال تو خیلی خوب آمد!"</p>
                    <p>بعد از فال، پدربزرگ به همه عیدی می‌دهد.</p>
                    <p>سال نو با لبخند و شعر آغاز می‌شود.</p>
                  </div>
                  
                  <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <h6 className="font-semibold text-gray-900 dark:text-white mb-2">English Translation:</h6>
                    <div className="space-y-2 text-gray-700 dark:text-gray-300 text-sm ltr text-left">
                      <p>On New Year's Eve, the Rad family is sitting around the Haft-Seen table.</p>
                      <p>Father says: "Let's take a Hafez fortune!"</p>
                      <p>Mother brings the Divan of Hafez. It's Nazli's turn.</p>
                      <p>She closes her eyes and makes a wish.</p>
                      <p>Then she opens the book. The poem is:</p>
                      <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4 my-4 text-center">
                        <p className="text-gray-700 dark:text-gray-300 font-medium italic">
                          "Spring has come to forgive hearts,<br/>
                          To pick a flower from our soil and dry it."
                        </p>
                      </div>
                      <p>Nazli smiles. Father says: "Your fortune came very well!"</p>
                      <p>After the fortune, grandfather gives everyone New Year gifts.</p>
                      <p>The New Year begins with smiles and poetry.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Original stories from API (if any) */}
              {(holidayPack.stories || []).length > 0 && (
                <div className="mb-8">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    داستان‌های اضافی (Additional Stories)
                  </h4>
                  <div className="space-y-6">
                    {(holidayPack.stories || []).map((story, index) => (
                      <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                        <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
                          {story.title}
                        </h4>
                        <p className="text-gray-900 dark:text-white leading-relaxed mb-4">
                          {story.content}
                        </p>
                        <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-3">
                          <p className="text-sm text-gray-900 dark:text-white">
                            <strong>پیام اخلاقی:</strong> {story.moral}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Games Tab */}
          {activeTab === 'games' && (
            <div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                بازی‌های آموزشی
              </h3>
              
              <div className="space-y-8">
                {/* Game 1: Haftseen Guessing */}
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg">
                  <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                    <span className="mr-2">🎲</span>
                    بازی ۱: حدس بزن چی نباید روی سفره باشه؟
                  </h4>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    روی آیتم‌هایی که نباید روی سفره هفت‌سین باشند کلیک کن!
                  </p>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                    {[
                      { id: 1, name: 'سبزه', correct: true },
                      { id: 2, name: 'سمنو', correct: true },
                      { id: 3, name: 'سیب', correct: true },
                      { id: 4, name: 'ساعت', correct: true },
                      { id: 5, name: 'سوسک', correct: false },
                      { id: 6, name: 'سکه', correct: true },
                    ].map((item) => (
                      <button
                        key={item.id}
                        onClick={() => {
                          const result = item.correct 
                            ? '❌ اشتباه! این یکی جزو سفره هفت‌سین هست.' 
                            : '✅ آفرین! این روی سفره هفت‌سین نیست.';
                          alert(result);
                        }}
                        className="p-3 bg-orange-50 dark:bg-orange-900/20 border-2 border-orange-200 dark:border-orange-700 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/40 transition-colors text-gray-900 dark:text-white font-medium"
                      >
                        {item.name}
                      </button>
                    ))}
                  </div>
                  
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      <strong>💡 نکته:</strong> فقط آیتم‌هایی که با حرف "س" شروع می‌شوند و معنای خاصی دارند روی سفره هفت‌سین قرار می‌گیرند.
                    </p>
                  </div>
                </div>

                {/* Game 2: Memory Match */}
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg">
                  <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                    <span className="mr-2">🧠</span>
                    بازی ۲: حافظه نوروزی
                  </h4>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    کلمات فارسی و انگلیسی را به خاطر بسپار!
                  </p>
                  
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-6 text-center">
                    <div className="mb-4">
                      <p className="text-lg text-gray-900 dark:text-white mb-2">
                        <strong>کلمه فارسی:</strong>
                      </p>
                      <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                        {[
                          { farsi: 'سیب', english: 'Apple' },
                          { farsi: 'سبزه', english: 'Sprouts' },
                          { farsi: 'سکه', english: 'Coin' },
                          { farsi: 'سمنو', english: 'Wheat Pudding' },
                        ][0].farsi}
                      </p>
                    </div>
                    
                    <div className="mb-4">
                      <p className="text-lg text-gray-900 dark:text-white mb-2">
                        <strong>معنای انگلیسی:</strong>
                      </p>
                      <p className="text-xl font-bold text-green-600 dark:text-green-400 ltr text-left">
                        {[
                          { farsi: 'سیب', english: 'Apple' },
                          { farsi: 'سبزه', english: 'Sprouts' },
                          { farsi: 'سکه', english: 'Coin' },
                          { farsi: 'سمنو', english: 'Wheat Pudding' },
                        ][0].english}
                      </p>
                    </div>
                    
                    <button 
                      onClick={() => alert('🎯 عالی! حالا سعی کن این کلمات را حفظ کنی.')}
                      className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                    >
                      حفظ کردم!
                    </button>
                  </div>
                  
                  <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { farsi: 'سیب', english: 'Apple' },
                      { farsi: 'سبزه', english: 'Sprouts' },
                      { farsi: 'سکه', english: 'Coin' },
                      { farsi: 'سمنو', english: 'Wheat Pudding' },
                    ].map((pair, index) => (
                      <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 text-center">
                        <p className="font-bold text-gray-900 dark:text-white mb-1">{pair.farsi}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400 ltr text-left">{pair.english}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Game 3: Charades */}
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg">
                  <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                    <span className="mr-2">🎭</span>
                    بازی ۳: پانتومیم نوروزی
                  </h4>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    این کارها را بدون حرف زدن نشان بده!
                  </p>
                  
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-6 text-center">
                    <p className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                      کار فعلی:
                    </p>
                    <p className="text-xl font-bold text-yellow-600 dark:text-yellow-400 mb-4">
                      پریدن از روی آتش
                    </p>
                    <button 
                      onClick={() => alert('🎭 حالا این کار را بدون حرف زدن برای دوستانت نشان بده!')}
                      className="px-6 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
                    >
                      شروع کن!
                    </button>
                  </div>
                  
                  <div className="mt-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      <strong>سایر کارها:</strong>
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {[
                        'گرفتن عیدی',
                        'چیدن سفره هفت‌سین',
                        'خانه‌تکانی',
                      ].map((item, index) => (
                        <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 text-center">
                          <p className="text-gray-900 dark:text-white">{item}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Game 4: Dialogue Practice */}
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg">
                  <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                    <span className="mr-2">💬</span>
                    بازی ۴: دیالوگ نوروزی
                  </h4>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    این موقعیت‌ها را تمرین کن!
                  </p>
                  
                  <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-6">
                    <p className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                      موقعیت فعلی:
                    </p>
                    <p className="text-lg text-purple-600 dark:text-purple-400 mb-4">
                      دوستت را در خیابان می‌بینی، سال نو را تبریک بگو.
                    </p>
                    
                    <div className="space-y-3">
                      <button 
                        onClick={() => alert('✅ عالی! "نوروز مبارک! سال نو مبارک!"')}
                        className="w-full p-3 bg-green-100 dark:bg-green-900/20 border border-green-300 dark:border-green-700 rounded-lg text-green-800 dark:text-green-200 hover:bg-green-200 dark:hover:bg-green-900/40 transition-colors"
                      >
                        نوروز مبارک! سال نو مبارک!
                      </button>
                      <button 
                        onClick={() => alert('✅ خوب! "سال نو مبارک! امیدوارم سال خوبی داشته باشی!"')}
                        className="w-full p-3 bg-blue-100 dark:bg-blue-900/20 border border-blue-300 dark:border-blue-700 rounded-lg text-blue-800 dark:text-blue-200 hover:bg-blue-200 dark:hover:bg-blue-900/40 transition-colors"
                      >
                        سال نو مبارک! امیدوارم سال خوبی داشته باشی!
                      </button>
                      <button 
                        onClick={() => alert('❌ این مناسب نیست. بهتر است از عبارات نوروزی استفاده کنی.')}
                        className="w-full p-3 bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-lg text-red-800 dark:text-red-200 hover:bg-red-200 dark:hover:bg-red-900/40 transition-colors"
                      >
                        سلام! چطوری؟
                      </button>
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      <strong>سایر موقعیت‌ها:</strong>
                    </p>
                    <div className="space-y-2">
                      {[
                        'مامان‌بزرگ بهت عیدی می‌دهد، چه می‌گویی؟',
                        'از مهمان‌ها درباره هفت‌سین سؤال کن.',
                      ].map((item, index) => (
                        <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                          <p className="text-gray-900 dark:text-white text-sm">{item}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Game 5: Nowruz Quiz */}
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg">
                  <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                    <span className="mr-2">📝</span>
                    بازی ۵: آزمون نوروزی
                  </h4>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    دانش خود را درباره نوروز محک بزن!
                  </p>
                  
                  <div className="space-y-6">
                    {/* Question 1 */}
                    <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-4">
                      <p className="font-bold text-gray-900 dark:text-white mb-3">
                        ۱. کدام آیتم جزو سفره هفت‌سین نیست؟
                      </p>
                      <div className="space-y-2">
                        {[
                          { text: 'سیب', correct: false },
                          { text: 'سمنو', correct: false },
                          { text: 'سوسک', correct: true },
                          { text: 'سکه', correct: false },
                        ].map((option, index) => (
                          <button
                            key={index}
                            onClick={() => {
                              if (option.correct) {
                                alert('✅ درست! سوسک جزو سفره هفت‌سین نیست.');
                              } else {
                                alert('❌ اشتباه! این آیتم جزو سفره هفت‌سین است.');
                              }
                            }}
                            className="w-full p-3 text-left bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                          >
                            {String.fromCharCode(65 + index)}. {option.text}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Question 2 */}
                    <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-4">
                      <p className="font-bold text-gray-900 dark:text-white mb-3">
                        ۲. چهارشنبه‌سوری چه زمانی برگزار می‌شود؟
                      </p>
                      <div className="space-y-2">
                        {[
                          { text: 'شب سال نو', correct: false },
                          { text: 'آخرین چهارشنبه قبل از نوروز', correct: true },
                          { text: 'روز سیزدهم فروردین', correct: false },
                          { text: 'اول فروردین', correct: false },
                        ].map((option, index) => (
                          <button
                            key={index}
                            onClick={() => {
                              if (option.correct) {
                                alert('✅ درست! چهارشنبه‌سوری آخرین چهارشنبه قبل از نوروز برگزار می‌شود.');
                              } else {
                                alert('❌ اشتباه! چهارشنبه‌سوری آخرین چهارشنبه قبل از نوروز است.');
                              }
                            }}
                            className="w-full p-3 text-left bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                          >
                            {String.fromCharCode(65 + index)}. {option.text}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Question 3 */}
                    <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-4">
                      <p className="font-bold text-gray-900 dark:text-white mb-3">
                        ۳. "عیدی" چیست؟
                      </p>
                      <div className="space-y-2">
                        {[
                          { text: 'نوعی غذا', correct: false },
                          { text: 'هدیه یا پول سال نو', correct: true },
                          { text: 'نوعی لباس', correct: false },
                          { text: 'نوعی بازی', correct: false },
                        ].map((option, index) => (
                          <button
                            key={index}
                            onClick={() => {
                              if (option.correct) {
                                alert('✅ درست! عیدی هدیه یا پولی است که بزرگ‌ترها به کوچکترها می‌دهند.');
                              } else {
                                alert('❌ اشتباه! عیدی هدیه یا پول سال نو است.');
                              }
                            }}
                            className="w-full p-3 text-left bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                          >
                            {String.fromCharCode(65 + index)}. {option.text}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Question 4 */}
                    <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-4">
                      <p className="font-bold text-gray-900 dark:text-white mb-3">
                        ۴. سیزده‌به‌در چه روزی است؟
                      </p>
                      <div className="space-y-2">
                        {[
                          { text: 'روز سیزدهم فروردین', correct: true },
                          { text: 'روز اول فروردین', correct: false },
                          { text: 'آخرین روز اسفند', correct: false },
                          { text: 'روز هفتم فروردین', correct: false },
                        ].map((option, index) => (
                          <button
                            key={index}
                            onClick={() => {
                              if (option.correct) {
                                alert('✅ درست! سیزده‌به‌در روز سیزدهم فروردین است.');
                              } else {
                                alert('❌ اشتباه! سیزده‌به‌در روز سیزدهم فروردین است.');
                              }
                            }}
                            className="w-full p-3 text-left bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                          >
                            {String.fromCharCode(65 + index)}. {option.text}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Question 5 */}
                    <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-4">
                      <p className="font-bold text-gray-900 dark:text-white mb-3">
                        ۵. هنگام پریدن از آتش چه می‌گویند؟
                      </p>
                      <div className="space-y-2">
                        {[
                          { text: 'سال نو مبارک', correct: false },
                          { text: 'زردی من از تو، سرخی تو از من', correct: true },
                          { text: 'نوروز مبارک', correct: false },
                          { text: 'بهار آمد', correct: false },
                        ].map((option, index) => (
                          <button
                            key={index}
                            onClick={() => {
                              if (option.correct) {
                                alert('✅ درست! "زردی من از تو، سرخی تو از من" گفته سنتی هنگام پریدن از آتش است.');
                              } else {
                                alert('❌ اشتباه! گفته سنتی "زردی من از تو، سرخی تو از من" است.');
                              }
                            }}
                            className="w-full p-3 text-left bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                          >
                            {String.fromCharCode(65 + index)}. {option.text}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Final Score Button */}
                    <div className="text-center">
                      <button
                        onClick={() => alert('🎉 تبریک! شما آزمون نوروزی را کامل کردید! امیدوارم چیزهای جدیدی یاد گرفته باشید.')}
                        className="px-8 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold rounded-lg hover:from-indigo-600 hover:to-purple-600 transition-all duration-300"
                      >
                        تکمیل آزمون
                      </button>
                    </div>
                  </div>
                </div>

                {/* Original games from API (if any) */}
                {(holidayPack.games || []).length > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      بازی‌های اضافی (Additional Games)
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {(holidayPack.games || []).map((game, index) => (
                        <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                          <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
                            {game.name}
                          </h4>
                          <p className="text-gray-600 dark:text-gray-400 mb-4">
                            {game.description}
                          </p>
                          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
                            <h5 className="font-semibold text-gray-900 dark:text-white mb-2">
                              دستورالعمل:
                            </h5>
                            <p className="text-sm text-gray-900 dark:text-white">
                              {game.instructions}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-center space-x-4 rtl:space-x-reverse">
        <button className="px-8 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-semibold rounded-lg hover:from-blue-600 hover:to-indigo-600 transition-all duration-300">
          شروع یادگیری
        </button>
        <Link
          to="/cultural/holidays"
          className="px-8 py-3 border-2 border-blue-500 text-blue-500 font-semibold rounded-lg hover:bg-blue-50 transition-all duration-300"
        >
          بازگشت به لیست
        </Link>
      </div>
      </div>
    </div>
  );
};

export default HolidayPackDetail; 